import { isEqual } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import MailIcon from '../../assets/svg/MailIcon';
import { FiltersPanel, ReportTable } from '../../components/BookingsReport';
import {
  ActivityLogModal,
  RescheduleDemoModal,
  SendReportModal,
} from '../../components/Modals';
import { Button, ButtonExport } from '../../components/Shared';
import {
  DATE_TIME_FORMAT,
  REPORT_FILE_FORMAT,
  USER_ROLE,
} from '../../constants';
import { saveCSVReport, saveXLSXReport } from '../../helpers';
import { useAuthContext } from '../../hooks';
import { Layout } from '../../layouts';

export const BookingsReport = () => {
  const { userRole } = useAuthContext();

  const isManager = userRole === USER_ROLE.MANAGER;

  const [selectedApptIDs, setSelectedApptIDs] = useState([]);

  const [isActivityLogModalOpened, setIsActivityLogModalOpened] =
    useState(false);

  const [bookingHistoryApptID, setBookingHistoryApptID] = useState(null);

  const openActivityLogModal = apptID => {
    setIsActivityLogModalOpened(true);
    setBookingHistoryApptID(apptID);
  };
  const closeActivityLogModal = () => {
    setIsActivityLogModalOpened(false);
    setBookingHistoryApptID(null);
  };

  const [isRescheduleDemoModalOpened, setIsRescheduleDemoModalOpened] =
    useState(false);

  const [reportToReschedule, setReportToReschedule] = useState(null);

  const openRescheduleDemoModal = report => {
    setIsRescheduleDemoModalOpened(true);
    setReportToReschedule(report);
  };
  const closeRescheduleDemoModal = () => {
    setIsRescheduleDemoModalOpened(false);
    setReportToReschedule(null);
  };

  const [isSendReportModalOpened, setIsSendReportModalOpened] = useState(false);

  const openSendReportModal = () => setIsSendReportModalOpened(true);
  const closeSendReportModal = () => setIsSendReportModalOpened(false);

  const initialFilters = {
    ...(isManager && { callerIds: [] }),
    startDate: moment(new Date()).format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH),
    endDate: moment(new Date()).format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH),
    statuses: [],
    apptType: null,
    isRescheduled: false,
  };

  const [filters, setFilters] = useState(initialFilters);

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const [managerTeam, setManagerTeam] = useState([]);
  const [isManagerTeamLoading, setIsManagerTeamLoading] = useState(true);
  const [isManagerTeamError, setIsManagerTeamError] = useState(false);

  const fetchManagerTeam = useCallback(async () => {
    setIsManagerTeamLoading(true);
    setIsManagerTeamError(false);

    try {
      const response = await fetch('/get-manager-team');
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setIsManagerTeamLoading(false);
      setManagerTeam(data.team);
    } catch {
      setIsManagerTeamLoading(false);
      setIsManagerTeamError(true);
      toast.error('Failed to load manager team!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    if (!isManager) return;
    fetchManagerTeam();
  }, [isManager, fetchManagerTeam]);

  const abortControllerRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [isReportsLoading, setIsReportsLoading] = useState(true);
  const [isReportsError, setIsReportsError] = useState(false);

  const fetchReports = useCallback(async () => {
    setIsReportsLoading(true);
    setIsReportsError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/reportsdata', {
        signal: abortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...filters,
          isRescheduled: filters.isRescheduled ? '1' : '0',
        }),
      });
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      const reports = data.reportsData.query1;
      setIsReportsLoading(false);
      setSelectedApptIDs(prev =>
        prev.filter(ApptID => reports.find(report => report.ApptID === ApptID)),
      );
      setReports(reports);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsReportsLoading(false);
      setIsReportsError(true);
    }
  }, [filters]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const [isExportReportsSubmitting, setIsExportReportsSubmitting] =
    useState(false);

  const exportSelectedReports = async reportFormat => {
    setIsExportReportsSubmitting(true);

    try {
      const response = await fetch('/export-reports-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportFormat, ApptIDs: selectedApptIDs }),
      });

      if (response.status !== 200) throw new Error();

      if (reportFormat === REPORT_FILE_FORMAT.XLSX) {
        const workBook = await response.json();
        saveXLSXReport(workBook, 'Bookings Report');
      }

      if (reportFormat === REPORT_FILE_FORMAT.CSV) {
        const blob = await response.blob();
        saveCSVReport(blob, 'Bookings Report');
      }

      setIsExportReportsSubmitting(false);
    } catch {
      setIsExportReportsSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const isFiltersChanged = !isEqual(initialFilters, filters);
  const areReportActionsDisabled =
    selectedApptIDs.length === 0 || isReportsLoading;
  const isButtonExportDisabled =
    areReportActionsDisabled || isExportReportsSubmitting;
  const isButtonSendReportDisabled = areReportActionsDisabled;
  const numberReports = reports.length;
  const hasReports = numberReports > 0;
  const numberSelectedReports = selectedApptIDs.length;
  const hasSelectedReports = numberSelectedReports > 0;

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex justify-between gap-3 overflow-auto shrink-0">
          <div className="flex gap-3 shrink-0">
            <FiltersPanel
              isManager={isManager}
              isManagerTeamLoading={isManagerTeamLoading}
              isManagerTeamError={isManagerTeamError}
              filters={filters}
              onChangeFilters={setFilters}
              managerTeam={managerTeam}
            />
            {isFiltersChanged && (
              <Button
                title="Clear All"
                variant="text"
                onClick={resetFilters}
                className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
              />
            )}
          </div>
          <div className="flex gap-3 shrink-0">
            {hasReports && (
              <div className="flex items-center gap-1">
                <span className="text-base-1000 font-medium text-[16px]/[24px]">
                  {hasSelectedReports
                    ? `${numberSelectedReports}/${numberReports}`
                    : numberReports}
                </span>
                <span className="text-base-1000 text-[16px]/[24px]">
                  {hasSelectedReports ? 'selected' : 'records'}
                </span>
              </div>
            )}
            {isManager && (
              <>
                <ButtonExport
                  disabled={isButtonExportDisabled}
                  onClick={exportSelectedReports}
                />
                <Button
                  title="Send Report"
                  variant="blue"
                  iconStart={
                    <MailIcon
                      className={twMerge(
                        'w-5 h-5 text-brand-900',
                        isButtonSendReportDisabled && 'text-base-400',
                      )}
                    />
                  }
                  disabled={isButtonSendReportDisabled}
                  onClick={openSendReportModal}
                />
              </>
            )}
          </div>
        </div>
        <ReportTable
          isManager={isManager}
          reports={reports}
          selectedApptIDs={selectedApptIDs}
          isReportsLoading={isReportsLoading}
          isReportsError={isReportsError}
          fetchReports={fetchReports}
          onChangeSelectedApptIDs={setSelectedApptIDs}
          openActivityLogModal={openActivityLogModal}
          openRescheduleDemoModal={openRescheduleDemoModal}
          onChangeReports={setReports}
          filterByStatuses={filters.statuses}
        />
        {isActivityLogModalOpened && (
          <ActivityLogModal
            open={isActivityLogModalOpened}
            apptID={bookingHistoryApptID}
            onClose={closeActivityLogModal}
          />
        )}
        {isRescheduleDemoModalOpened && (
          <RescheduleDemoModal
            open={isRescheduleDemoModalOpened}
            reportToReschedule={reportToReschedule}
            onClose={closeRescheduleDemoModal}
            fetchReports={fetchReports}
          />
        )}
        {isSendReportModalOpened && (
          <SendReportModal
            open={isSendReportModalOpened}
            requestURL="/send-reports"
            requestBody={{ ApptIDs: selectedApptIDs }}
            onClose={closeSendReportModal}
          />
        )}
      </div>
    </Layout>
  );
};
